import {
  OrderInternalDocumentInputModel,
  OrderInternalDocumentViewModel,
  OrderInternalFormFieldViewModel,
  OrderInternalInputModel,
  OrderInternalViewModel,
  OrderSuppliersDocumentViewModel,
  OrderSuppliersFormFieldViewModel,
  OrderSuppliersInputModel,
  OrderSuppliersViewModel,
  WorkflowOrderStepStatus,
} from '@ail/orders-api'
import { WorkflowStatus, WorkflowViewModel } from '@ail/workflows-api'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../auth/UseAuth'
import { Arr } from '../../../helpers/Arr'
import { downloadFile } from '../../../helpers/FileDownload'
import { FileHelper } from '../../../helpers/FileHelper'
import { parseFileData } from '../../../helpers/Utils'
import {
  isSupplierOrder,
  useLazyGetOrderInternalDocumentQuery,
  useLazyGetOrderSuppliersDocumentQuery,
  useUpdateOrderInternalMutation,
  useUpdateOrderSuppliersMutation,
} from '../../../redux/api/ordersApi'
import { useLazyGetWorkflowQuery } from '../../../redux/api/workflowsApi'
import { Button } from '../../common/Buttons/Button'
import { ButtonType, TransparentButton } from '../../common/Buttons/TransparentButton'
import { Dialog } from '../../common/Dialog/Dialog'
import { WorkflowPreviewModal } from '../WorkflowPreviewModal/WorkflowPreviewModal'
import './DocumentsModal.scss'
import { Loader } from '../../common/Loader/Loader'

export interface DocumentsModalProps {
  data: {
    order: OrderInternalViewModel | OrderSuppliersViewModel
    field: OrderInternalFormFieldViewModel | OrderSuppliersFormFieldViewModel
  }
  onClose: () => void
}

export type Document = OrderInternalDocumentViewModel | OrderSuppliersDocumentViewModel

export type DocPreviewData = {
  document: Document
  order: OrderInternalViewModel | OrderSuppliersViewModel
  field: OrderInternalFormFieldViewModel | OrderSuppliersFormFieldViewModel
}
const READONLY_STATUSES = [
  WorkflowOrderStepStatus.Done,
  WorkflowOrderStepStatus.Cancelled,
  WorkflowOrderStepStatus.Archived,
]

const MaxFileSizeMegabytes = 8
const MaxFileSize = MaxFileSizeMegabytes * 1000 * 1000

export function DocumentsModal(props: DocumentsModalProps) {
  const [currentDocuments, setCurrentDocuments] = useState<Document[]>([])
  const [classNames, setClassNames] = useState<Record<string, string | undefined>>({})
  const [pending, setPending] = useState<Record<string, OrderInternalDocumentInputModel | undefined>>({})
  const [loading, setLoading] = useState(false)
  const { t, i18n } = useTranslation()
  const { user } = useAuth()

  const [updateOrderInternal] = useUpdateOrderInternalMutation()
  const [updateOrderSuppliers] = useUpdateOrderSuppliersMutation()

  const [downloadInternal] = useLazyGetOrderInternalDocumentQuery()
  const [downloadSuppliers] = useLazyGetOrderSuppliersDocumentQuery()
  const [getWorkflow] = useLazyGetWorkflowQuery()
  const [relevantWorkflows, setRelevantWorkflows] = useState<Record<string, WorkflowViewModel | undefined>>({})
  const [workflowsLoading, setWorkflowsLoading] = useState(false)

  const genericDocuments = currentDocuments.filter((d) => !d.required)
  const mandatoryDocuments = currentDocuments.filter((d) => !!d.required)
  const canSave = Object.values(pending).some((p) => !!p)

  const [docToPreview, setDocToPreview] = useState<DocPreviewData | undefined>(undefined)

  useEffect(() => {
    setCurrentDocuments(props.data.field.documents ?? [])

    const workflowIds = props.data.field.documents.filter((d) => !!d.workflowId).map((d) => d.workflowId!)

    setRelevantWorkflows({})
    if (!workflowIds.length) {
      setWorkflowsLoading(false)
      return
    }

    // A failing workflows api must not hide the statuses that could be loaded,
    // so every workflow is resolved on its own and failures are simply left out.
    let cancelled = false
    setWorkflowsLoading(true)
    Promise.allSettled(workflowIds.map((id) => getWorkflow(id, false).unwrap())).then((results) => {
      if (cancelled) {
        return
      }
      const workflows = results
        .filter((r) => r.status === 'fulfilled')
        .map((r) => r.value?.workflow)
        .filter((w) => !!w)
      setRelevantWorkflows(
        Arr.toObject(
          workflows,
          (w) => w.id,
          (w) => w
        )
      )
      setWorkflowsLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [getWorkflow, props.data.field.documents])

  const handleDragEnter = (document: Document, e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setClassNames({
      ...classNames,
      [document.name]: 'drag-entered',
    })
  }

  const handleDragLeave = (document: Document, e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setClassNames({
      ...classNames,
      [document.name]: '',
    })
  }

  const handleDrop = async (document: Document, e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (e.dataTransfer.files.length !== 1) {
      return
    }

    if (e.dataTransfer.files.item(0)!.size > MaxFileSize) {
      alert(`File size should not exceed ${MaxFileSizeMegabytes}Mb`)
      return
    }

    const fileName = e.dataTransfer.files.item(0)!.name
    const content = await FileHelper.fileToDataURL(e.dataTransfer.files.item(0)!)
    setPending({
      ...pending,
      [document.name]: {
        name: document.name,
        content: `${fileName}|${content!}`,
        workflowId: document.workflowId,
      },
    })
    setClassNames({
      ...classNames,
      [document.name]: 'file-dropped',
    })
  }

  const handleFileSelected = async (document: Document, e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length !== 1) {
      return
    }

    if (e.target.files.item(0)!.size > MaxFileSize) {
      alert(`File size should not exceed ${MaxFileSizeMegabytes}Mb`)
      return
    }

    const fileName = e.target.files.item(0)!.name
    const content = await FileHelper.fileToDataURL(e.target.files.item(0)!)
    setPending({
      ...pending,
      [document.name]: {
        name: document.name,
        content: `${fileName}|${content!}`,
        workflowId: document.workflowId,
      },
    })
    setClassNames({
      ...classNames,
      [document.name]: 'file-dropped',
    })
  }

  const handleFileDeleted = (document: Document) => {
    setPending({
      ...pending,
      [document.name]: {
        name: document.name,
        content: document.documentId ? '' : undefined,
        workflowId: document.workflowId,
      },
    })
    setClassNames({
      ...classNames,
      [document.name]: undefined,
    })
  }

  const submit = async () => {
    const model: OrderInternalInputModel | OrderSuppliersInputModel = {
      form: {
        status: props.data.order.form.status,
        fields: [
          {
            name: props.data.field.name,
            documents: Object.values(pending)
              .filter((p) => !!p)
              .map((p) => p!),
          },
        ],
      },
    }

    try {
      setLoading(true)
      let result: OrderSuppliersViewModel | OrderInternalViewModel | undefined = undefined
      if (isSupplierOrder(props.data.order)) {
        if (model.form.status === WorkflowOrderStepStatus.New) {
          model.form.status = WorkflowOrderStepStatus.InProgress
        }
        result = await updateOrderSuppliers({ id: props.data.order.id, model }).unwrap()
      } else {
        result = await updateOrderInternal({ id: props.data.order.id, model }).unwrap()
      }
      const field = result.form.fields.find((f) => f.type === props.data.field.type && f.name === props.data.field.name)
      setCurrentDocuments(field?.documents ?? [])
    } finally {
      setPending({})
      setClassNames({})
      setLoading(false)
      props.onClose()
    }
  }

  const download = async (document: Document) => {
    try {
      let result: OrderInternalDocumentViewModel | OrderSuppliersDocumentViewModel | undefined = undefined
      if (isSupplierOrder(props.data.order)) {
        result = await downloadSuppliers({ id: props.data.order.id, documentId: document.documentId! }).unwrap()
      } else {
        result = await downloadInternal({ id: props.data.order.id, documentId: document.documentId! }).unwrap()
      }

      const fileData = parseFileData(result.content)
      if (fileData) {
        downloadFile(fileData)
      }
    } finally {
      setPending({})
      setClassNames({})
    }
  }

  const renderDocument = (doc: Document) => {
    const field = props.data.field
    const readonly = props.data.field.readonly || READONLY_STATUSES.includes(props.data.order.form.status)
    const deletePending = pending[doc.name] && !pending[doc.name]?.content
    const uploadPending = !!pending[doc.name]?.content

    const canDelete = !readonly && ((doc.documentId && !deletePending) || uploadPending)
    const canDownload = !!doc.documentId && !deletePending && !uploadPending
    const canUpload = !readonly && !uploadPending && (!doc.documentId || deletePending)
    const hasWorkflow = canDownload && !!doc.workflowKind

    const _wf = doc.workflowId ? relevantWorkflows[doc.workflowId] : undefined
    const status = _wf?.status ?? ''
    // The workflow exists, but its status could not be fetched (workflows api unresponsive).
    const statusUnavailable = !!doc.workflowId && !workflowsLoading && !_wf
    const workflowStatus = !doc.workflowId
      ? 'canStart'
      : workflowsLoading
        ? 'statusLoading'
        : statusUnavailable
          ? 'statusUnavailable'
          : status === WorkflowStatus.Complete && _wf?.hasErrors
            ? 'hasError'
            : status
    const workflowBtnText = !doc.workflowId
      ? t('START_WORKFLOW')
      : workflowsLoading
        ? t('LOADING')
        : statusUnavailable
          ? t('WORKFLOWUNAVAILABLE')
          : t(status.toUpperCase())

    const dropBoxLabel = canUpload
      ? t('DRAG_AND_DROP_YOUR_FILE')
      : uploadPending
        ? pending[doc.name]?.content?.split('|').at(0)
        : (doc.fileName ?? t('NO_DOCUMENTS_FOUND'))
    let documentName = doc.tr?.[i18n.language]
    documentName ??= field.documents.length === 1 ? (field.tr?.[i18n.language] ?? field.name) : doc.name
    return (
      <div key={doc.name} className="document">
        <div className="document-name">
          <div className="document-name-left">
            {doc.documentId && <i className="icon-check" />}
            {!doc.documentId && doc.required && <i className="icon-check disabled" />}
            {documentName}
          </div>
          <div className="document-name-right">
            {hasWorkflow && user?.isInternalUser && (
              <Button
                onClick={() => {
                  setDocToPreview({ document: doc, order: props.data.order, field: props.data.field })
                }}
                text={workflowBtnText}
                icon={`icon ${workflowStatus} `}
                className={`previewButton transparent-button ${workflowStatus}`}
              ></Button>
            )}
          </div>
        </div>
        <div
          className={`document-drop-box ${classNames[doc.name] ?? ''}`}
          onDragEnter={(e) => handleDragEnter(doc, e)}
          onDragLeave={(e) => handleDragLeave(doc, e)}
          onDragOver={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onDrop={(e) => handleDrop(doc, e)}
        >
          <span>{dropBoxLabel}</span>
          <div className="document-drop-box-actions">
            {canUpload && (
              <i className="icon-upload">
                <input type="file" onChange={(e) => handleFileSelected(doc, e)} />
              </i>
            )}
            {canDownload && <i className="icon-download" onClick={() => download(doc)} />}
            {canDelete && <i className="icon-clear" onClick={() => handleFileDeleted(doc)} />}
          </div>
        </div>
      </div>
    )
  }

  return docToPreview ? (
    <WorkflowPreviewModal
      data={docToPreview}
      onClose={() => {
        props.onClose()
        setDocToPreview(undefined)
      }}
      onCancel={() => setDocToPreview(undefined)}
    />
  ) : (
    <Dialog open={!!props.data} onClose={props.onClose} width={650}>
      <Loader blocking={loading}>
        <div className="documents-modal">
          <div className="documents-modal-content">
            {!!genericDocuments?.length && (
              <div>
                <div className="category-name">
                  <i className="icon-doc"></i>
                  {!mandatoryDocuments?.length ? t('DOCUMENTS') : t('GENERIC_DOCUMENTS')}
                </div>
                {genericDocuments.map((d) => renderDocument(d))}
              </div>
            )}

            {!!mandatoryDocuments?.length && (
              <div>
                <div className="category-name">
                  <i className="icon-protocol"></i>
                  {t('MANDATORY_DOCUMENTS')}
                </div>
                {mandatoryDocuments.map((d) => renderDocument(d))}
              </div>
            )}
          </div>
          <div className="documents-modal-actions">
            <TransparentButton
              type={ButtonType.Negative}
              icon="icon-close"
              onClick={props.onClose}
              name={t('CANCEL')}
            />
            <TransparentButton
              type={ButtonType.Positive}
              icon="icon-check"
              onClick={submit}
              name={t('CONFIRM')}
              disabled={!canSave}
            />
          </div>
        </div>
      </Loader>
    </Dialog>
  )
}
